import { PolicyLayout } from '../components/PolicyLayout';
import { ContactBlock } from '../components/ContactBlock';

const SECTIONS = [
  { id: 'what-are-cookies', title: 'What Are Cookies' },
  { id: 'how-we-use', title: 'How We Use Cookies and Local Storage' },
  { id: 'cookie-categories', title: 'Categories of Cookies' },
  { id: 'essential-cookies', title: 'Essential Cookies' },
  { id: 'analytics-cookies', title: 'Analytics Cookies' },
  { id: 'marketing-cookies', title: 'Marketing Cookies' },
  { id: 'third-party', title: 'Third-Party Services' },
  { id: 'managing-cookies', title: 'Managing Your Cookie Preferences' },
  { id: 'browser-settings', title: 'Browser Settings' },
  { id: 'impact', title: 'Impact of Disabling Cookies' },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' },
];

export const CookiePolicy = () => {
  return (
    <PolicyLayout title="Cookie Policy" lastUpdated="22 April 2026" sections={SECTIONS}>
      <section id="what-are-cookies">
        <h2>What Are Cookies</h2>
        <ul>
          <li>
            Cookies are small text files stored on your device when you visit a website. They help
            the website remember your preferences and activity.
          </li>
          <li>
            We also use similar technologies including local storage (localStorage), which stores
            data in your browser to maintain your session and preferences.
          </li>
          <li>This policy covers all such storage mechanisms used by NursingCareBooking.com.</li>
          <li>
            Cookies and local storage can be "session" based (deleted when you close your browser)
            or "persistent" (remain on your device until they expire or you delete them).
          </li>
        </ul>
      </section>

      <section id="how-we-use">
        <h2>How We Use Cookies and Local Storage</h2>
        <table>
          <thead>
            <tr>
              <th>Name/Key</th>
              <th>Type</th>
              <th>Purpose</th>
              <th>Duration</th>
              <th>Category</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>authToken</td>
              <td>localStorage</td>
              <td>Stores your authentication token to keep you logged in as you navigate the Platform</td>
              <td>Until logout or token expiry</td>
              <td>Essential</td>
            </tr>
            <tr>
              <td>userData</td>
              <td>localStorage</td>
              <td>Stores your user session data (role, profile status) to personalise your experience</td>
              <td>Until logout</td>
              <td>Essential</td>
            </tr>
            <tr>
              <td>cookieConsent</td>
              <td>localStorage</td>
              <td>Remembers your cookie consent preferences so we do not ask repeatedly</td>
              <td>1 year</td>
              <td>Essential</td>
            </tr>
            <tr>
              <td>_ga</td>
              <td>Cookie</td>
              <td>Google Analytics - Distinguishes unique users by assigning a randomly generated number</td>
              <td>2 years</td>
              <td>Analytics</td>
            </tr>
            <tr>
              <td>_ga_KGSSCQNNEC</td>
              <td>Cookie</td>
              <td>Google Analytics - Maintains session state and tracks interactions</td>
              <td>2 years</td>
              <td>Analytics</td>
            </tr>
            <tr>
              <td>_fbp</td>
              <td>Cookie</td>
              <td>Meta/Facebook Pixel - Identifies browsers for advertising and site analytics</td>
              <td>3 months</td>
              <td>Marketing</td>
            </tr>
            <tr>
              <td>fr</td>
              <td>Cookie</td>
              <td>Meta/Facebook Pixel - Delivers and measures advertising across Facebook services</td>
              <td>3 months</td>
              <td>Marketing</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section id="cookie-categories">
        <h2>Categories of Cookies</h2>
        <p>We classify our cookies into three categories:</p>
        <ul>
          <li>
            <strong>Essential</strong>: Required for the Platform to function. These cannot be
            disabled without losing core functionality such as authentication and session
            management.
          </li>
          <li>
            <strong>Analytics</strong>: Help us understand how visitors interact with the Platform
            by collecting and reporting information. This data is used to improve the user
            experience.
          </li>
          <li>
            <strong>Marketing</strong>: Used to track visitors across websites to display relevant
            advertisements and measure the effectiveness of marketing campaigns.
          </li>
        </ul>
      </section>

      <section id="essential-cookies">
        <h2>Essential Cookies</h2>
        <ul>
          <li>
            Essential cookies and local storage items are strictly necessary for the Platform to
            operate.
          </li>
          <li>
            They enable core functions such as user authentication, session management, and
            remembering your cookie preferences.
          </li>
          <li>Without these, you would not be able to log in or use the Platform's services.</li>
          <li>
            These are set automatically and cannot be disabled through our cookie consent mechanism.
          </li>
          <li>
            Legal basis: These are processed under the "strictly necessary" exemption under the
            Privacy and Electronic Communications Regulations (PECR).
          </li>
        </ul>
      </section>

      <section id="analytics-cookies">
        <h2>Analytics Cookies</h2>
        <ul>
          <li>
            We use Google Analytics (Tracking ID: G-KGSSCQNNEC) to understand how visitors use our
            Platform.
          </li>
          <li>
            Information collected includes: pages visited, time spent on pages, how you arrived at
            our Platform, interactions with Platform features (e.g., viewing provider profiles,
            starting bookings), device type, browser type, and general geographic location.
          </li>
          <li>
            Google Analytics data is anonymised and aggregated. We do not use it to identify
            individual users.
          </li>
          <li>
            These cookies are only set if you consent to analytics cookies through our cookie
            consent banner.
          </li>
          <li>
            You can also opt out of Google Analytics across all websites by installing the{' '}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Analytics Opt-out Browser Add-on
            </a>
            .
          </li>
        </ul>
      </section>

      <section id="marketing-cookies">
        <h2>Marketing Cookies</h2>
        <ul>
          <li>
            We use Meta/Facebook Pixel (Pixel ID: 1467140155014290) to measure the effectiveness of
            our advertising and to deliver relevant content.
          </li>
          <li>
            Events tracked include: page views, registration completions, search activity, provider
            profile views, and booking initiations.
          </li>
          <li>
            This data helps us understand which marketing efforts are effective and improve our
            communications.
          </li>
          <li>
            These cookies are only set if you consent to marketing cookies through our cookie
            consent banner.
          </li>
          <li>
            You can manage your Facebook advertising preferences at{' '}
            <a
              href="https://www.facebook.com/ads/preferences"
              target="_blank"
              rel="noopener noreferrer"
            >
              facebook.com/ads/preferences
            </a>
            .
          </li>
        </ul>
      </section>

      <section id="third-party">
        <h2>Third-Party Services</h2>
        <ul>
          <li>
            <strong>Google Analytics</strong>: Operated by Google LLC. Data may be processed in
            the United States. Google's privacy policy:{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://policies.google.com/privacy
            </a>
            . Google Analytics Terms of Service:{' '}
            <a
              href="https://marketingplatform.google.com/about/analytics/terms/us/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://marketingplatform.google.com/about/analytics/terms/us/
            </a>
          </li>
          <li>
            <strong>Meta/Facebook Pixel</strong>: Operated by Meta Platforms, Inc. Data may be
            processed in the United States. Meta's privacy policy:{' '}
            <a
              href="https://www.facebook.com/privacy/policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.facebook.com/privacy/policy/
            </a>
          </li>
        </ul>
        <p>
          We do not control the cookies set by these third-party services. Please refer to their
          respective privacy policies for more information about how they process your data.
        </p>
      </section>

      <section id="managing-cookies">
        <h2>Managing Your Cookie Preferences</h2>
        <p>
          When you first visit our Platform, a cookie consent banner will appear allowing you to
          choose which categories of cookies to accept.
        </p>
        <p>You can choose to:</p>
        <ul>
          <li>Accept All Cookies</li>
          <li>Accept Essential Only</li>
          <li>Customise your preferences (toggle analytics and marketing cookies individually)</li>
        </ul>
        <ul>
          <li>
            You can change your preferences at any time by clicking the cookie settings link in our
            website footer.
          </li>
          <li>
            Note: Essential cookies cannot be disabled as they are required for the Platform to
            function.
          </li>
        </ul>
      </section>

      <section id="browser-settings">
        <h2>Browser Settings</h2>
        <p>Instructions for managing cookies in major browsers:</p>
        <ul>
          <li>
            <strong>Google Chrome</strong>: Settings &gt; Privacy and Security &gt; Cookies and
            other site data
          </li>
          <li>
            <strong>Mozilla Firefox</strong>: Settings &gt; Privacy &amp; Security &gt; Cookies
            and Site Data
          </li>
          <li>
            <strong>Safari</strong>: Preferences &gt; Privacy &gt; Manage Website Data
          </li>
          <li>
            <strong>Microsoft Edge</strong>: Settings &gt; Cookies and site permissions &gt;
            Manage and delete cookies and site data
          </li>
        </ul>
        <ul>
          <li>For other browsers, please refer to the browser's help documentation.</li>
          <li>
            Note: Clearing cookies through browser settings will also clear your authentication
            session, requiring you to log in again.
          </li>
        </ul>
      </section>

      <section id="impact">
        <h2>Impact of Disabling Cookies</h2>
        <ul>
          <li>
            <strong>Essential cookies disabled</strong>: You will not be able to log in, and core
            Platform functionality will be unavailable.
          </li>
          <li>
            <strong>Analytics cookies disabled</strong>: We will not be able to collect usage data
            to improve the Platform. Your browsing experience will not be affected.
          </li>
          <li>
            <strong>Marketing cookies disabled</strong>: You may still see advertisements, but
            they will not be personalised based on your activity on our Platform.
          </li>
        </ul>
        <p>
          Disabling cookies does not prevent you from browsing the Platform as a visitor, but
          authenticated features (booking, messaging, reviews) require essential cookies.
        </p>
      </section>

      <section id="changes">
        <h2>Changes to This Policy</h2>
        <ul>
          <li>
            We may update this Cookie Policy to reflect changes in our practices or for legal,
            regulatory, or operational reasons.
          </li>
          <li>Changes will be posted on this page with an updated "Last updated" date.</li>
          <li>Material changes will be communicated through a notice on our Platform.</li>
        </ul>
      </section>

      <section id="contact">
        <h2>Contact Us</h2>
        <ContactBlock
          introText="If you have questions about our use of cookies:"
          extraContent={
            <p>
              For data protection concerns, you may also contact the Information Commissioner's
              Office (ICO) at{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
                ico.org.uk
              </a>
              .
            </p>
          }
        />
      </section>
    </PolicyLayout>
  );
};
